import * as moment from "moment";
import { useEffect, useState } from "react";
import { Archive } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import CustomTable from "../../../components/custom-table/custom-table";
import LetterUploadDoc from "../../../components/modals/LetterUploadDoc/letterUploadDoc";
import SftpDocModal from "../../../components/modals/sftpDocModal/sftpDocModal";
import SftpRerun from "../../../components/modals/SftpRerun/sftpRerun";
import TextNoteDoc from "../../../components/modals/TextNoteDoc/textNoteDoc";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { FilterSection, FilterValues } from "../../../data-model/filterValues";
import { ModalId } from "../../../enums/modalId";
import { TableNames } from "../../../enums/tableNames";
import { useModalOpener } from "../../../hooks/useModalOpener";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import {
  closeModal,
  setOpenedModalId,
} from "../../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { shouldOpenCmmModal } from "../../pharmaStpFile/table/utils/shouldOpenCmmModal";
import {
  handleOpenNonEpaHistory,
  handleOpenSubmissionSummary,
} from "../actionHandlers/openSubmissionSummaryHandler";
import { handleSaveSftpRerunAction } from "../actionHandlers/rerunModalSave";
import { useCallingWorklistTableData } from "../hooks/useCallingWorklistTableData";
import { useDenialLetterUploadScreenshot } from "../hooks/useDenialLetterUploadScreenshot";
import { useFilterAndSearch } from "../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../hooks/useInitialDataFetchingForTable";
import { useNextPageDataFetch } from "../hooks/useNextPageDataFetch";
import { useTextNoteScreenshot } from "../hooks/useTextNoteScreenshot";
import { useTextNotesSignedScreenshot } from "../hooks/useTextNotesSignedScreenshot";
import {
  CallingWorklistTableHeader,
  generateTableDataForCallingWorklistTable,
} from "../table/callingWorklistTableData";
import { shouldOpenLetterUploadModal } from "../table/utils/shouldOpenLetterUploadModal";
import { shouldOpenModal } from "../table/utils/shouldOpenModal";
import { CallingWorklistView } from "../utils/buildWorklistFilters";
import { createAllFilterSections } from "../utils/createFilterSections";

interface CallingWorklistTableProps {
  /**
   * Worklist to fetch. Non-ePA uses `non_epa_required`; Pending uses
   * `epa_pending_required`. User-selected Response Status filters apply to both.
   */
  view?: CallingWorklistView;
  /** Table this list owns filter, search and pagination state under. */
  tableName?: TableNames;
}

const CallingWorklistTable = ({
  view = CallingWorklistView.NON_EPA,
  tableName = TableNames.CALLING_WORKLIST_ORDERS,
}: CallingWorklistTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId } = useSelector(
    (state: RootState) => state.modalSliceNew,
  );
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();
  const orgId = getOrgIdForFetchExternalWorklist();

  useModalOpener();

  const pageSize = 120;

  const [filterData, setFilterData] = useState<FilterSection[]>([]);
  const [allDataForFilters, setAllDataForFilters] = useState<any[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const { pharmaStpFileState, setPharmaStpFileState, tableData, setTableData } =
    useCallingWorklistTableData();

  const [isArchiveActive, setIsArchiveActive] = useState(false);

  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(
      setPharmaStpFileState,
      setTableData,
      pageSize,
      isArchiveActive,
      view,
      tableName,
    );

  const { paginationResetCount, setSearchTerm, searchTerm, validFilters } =
    useFilterAndSearch(
      initialDataFetchingExecuted,
      setTableData,
      setPharmaStpFileState,
      pageSize,
      isArchiveActive,
      view,
      tableName,
    );

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    pharmaStpFileState,
    setPharmaStpFileState,
    setTableData,
    pageSize,
    isArchiveActive,
    view,
    tableName,
  );

  const [allStpFileOrdersTableData, setAllStpFileOrdersTableData] = useState<
    any[]
  >([]);
  const [selectedIdentifier, setSelectedIdentifier] = useState<string | null>(
    null,
  );
  const [selectedLetterUploadIdentifier, setSelectedLetterUploadIdentifier] =
    useState<string | null>(null);
  const [selectedRerunIdentifier, setSelectedRerunIdentifier] = useState<
    string | null
  >(null);
  const [isRerunning, setIsRerunning] = useState(false);

  const {
    screenshotUrl,
    formattedTime: screenshotFormattedTime,
    loading: screenshotLoading,
    error: screenshotError,
  } = useTextNoteScreenshot(selectedIdentifier);

  const {
    signedScreenshotUrl,
    loading: signedScreenshotLoading,
    error: signedScreenshotError,
  } = useTextNotesSignedScreenshot(selectedIdentifier);

  const [letterUploadTimestamp, setLetterUploadTimestamp] =
    useState<string>("");

  const {
    screenshotUrl: letterUploadScreenshotUrl,
    loading: letterUploadLoading,
    error: letterUploadError,
  } = useDenialLetterUploadScreenshot(selectedLetterUploadIdentifier);

  useEffect(() => {
    const formattedTableData = generateTableDataForCallingWorklistTable(
      tableData,
      orgId,
    );
    setAllStpFileOrdersTableData(formattedTableData);

    if (
      tableData &&
      tableData.length > 0 &&
      (isFirstLoad || !validFilters || validFilters.length === 0)
    ) {
      if (isFirstLoad) {
        setIsFirstLoad(false);
      }

      setAllDataForFilters((prevData) => {
        const combinedData = [...prevData, ...tableData];

        const uniqueData = combinedData.reduce((acc, item) => {
          if (!acc.some((x: any) => x.identifier === item.identifier)) {
            acc.push(item);
          }
          return acc;
        }, [] as any[]);

        return uniqueData;
      });
    }
  }, [tableData, validFilters, isFirstLoad, orgId]);

  const handleSearch = (searchText: string) => {
    setSearchTerm(searchText);
  };

  const handleRefresh = () => {
    setInitialDataFetchingExecuted(false);
    setAllDataForFilters([]);
    setIsFirstLoad(true);
  };

  /**
   * Opens the SFTP / CMM document modal when the row's CMM RPA status allows viewing.
   *
   * @param rowData - Table row including `cmmStatus` from order meta
   */
  const handleViewCmmClick = (rowData: any) => {
    if (shouldOpenCmmModal(rowData.cmmStatus)) {
      dispatch(
        setOpenedModalId({
          id: ModalId.SFTP_DOC_MODAL,
          metaData: {
            selectedRowData: rowData,
          },
        }),
      );
    }
  };

  const handleCloseSftpDocModal = () => {
    dispatch(closeModal());
  };

  const handleTextNoteClick = (rowData: any) => {
    const identifier = rowData?.identifier;

    if (identifier && shouldOpenModal(rowData.textNoteStatus)) {
      setSelectedIdentifier(identifier);
      dispatch(
        setOpenedModalId({
          id: ModalId.TEXT_NOTE_DOC,
          metaData: {
            selectedRowData: rowData,
            identifier: identifier,
          },
        }),
      );
    } else {
      console.error("No identifier found in row data");
    }
  };

  const handleLetterUploadClick = (rowData: any) => {
    const identifier = rowData?.identifier;

    if (identifier && shouldOpenLetterUploadModal(rowData.letterUploadStatus)) {
      setSelectedLetterUploadIdentifier(identifier);
      const updatedAt = rowData?.letterUploadUpdatedAt;
      setLetterUploadTimestamp(
        updatedAt
          ? moment
              .utc(updatedAt)
              .add(5, "hours")
              .add(30, "minutes")
              .format("MMM DD, YYYY h:mm A")
          : "",
      );
      dispatch(
        setOpenedModalId({
          id: ModalId.LETTER_UPLOAD_DOC,
          metaData: {
            selectedRowData: rowData,
            identifier: identifier,
          },
        }),
      );
    }
  };

  const handleRowClick = (rowData: any) => {};

  const handleCloseTextNoteDocModal = () => {
    dispatch(closeModal());
    setSelectedIdentifier(null);
  };

  const handleSaveTextNoteDoc = () => {
    dispatch(closeModal());
    setSelectedIdentifier(null);
  };

  const handleCloseLetterUploadDocModal = () => {
    dispatch(closeModal());
    setSelectedLetterUploadIdentifier(null);
    setLetterUploadTimestamp("");
  };

  const handleDropdownSectionIconClick = () => {
    setIsArchiveActive((prev) => !prev);
  };

  const handleAddKeyButtonClick = (rowData: any) => {
    const rawData =
      tableData?.find((r: any) => r.identifier === rowData.identifier) ??
      rowData;
    handleOpenSubmissionSummary(dispatch, rawData);
  };

  /**
   * Opens the read-only non-EPA history side panel for the row.
   *
   * @param rowData - Table row for the clicked history icon
   */
  const handleCallingHistoryClick = (rowData: any) => {
    const rawData =
      tableData?.find((r: any) => r.identifier === rowData.identifier) ??
      rowData;
    handleOpenNonEpaHistory(dispatch, rawData);
  };

  useEffect(() => {
    if (allDataForFilters && allDataForFilters.length > 0) {
      const filters = createAllFilterSections(allDataForFilters);
      setFilterData(filters);
    }
  }, [allDataForFilters]);

  const handleFilterApply = (data: FilterValues) => {
    dispatch(setFilter(data));
  };

  const tableContextData = getTableDataFromContext(tableName);

  useEffect(() => {
    if (tableContextData?.shouldRefetch) {
      setInitialDataFetchingExecuted(false);
      setTableDataForContext(tableName, {
        shouldRefetch: false,
      });
    }
  }, [
    tableContextData?.shouldRefetch,
    setInitialDataFetchingExecuted,
    setTableDataForContext,
    tableName,
  ]);

  if (pharmaStpFileState.error) {
    console.error(
      "Error fetching calling worklist data:",
      pharmaStpFileState.error,
    );
  }

  const handleThreeDotsOptionClick = (optionId: string, rowId: string) => {
    if (optionId === "rerun_status") {
      setSelectedRerunIdentifier(rowId);
      dispatch(setOpenedModalId({ id: ModalId.SFTP_RERUN_MODAL }));
    }
  };
  const handleCloseSftpRerunModal = () => {
    dispatch(closeModal());
    setSelectedRerunIdentifier(null);
    setIsRerunning(false);
  };
  const handleSaveSftpRerun = () => {
    handleSaveSftpRerunAction({
      dispatch,
      selectedRerunIdentifier,
      setIsRerunning,
      setSelectedRerunIdentifier,
    });
  };

  return (
    <>
      <CustomTable
        tableHeaders={CallingWorklistTableHeader}
        tableData={allStpFileOrdersTableData}
        itemsPerPage={10}
        pagesPerView={4}
        count={allStpFileOrdersTableData.length}
        totalCount={pharmaStpFileState.data?.total_count ?? 0}
        isFetching={pharmaStpFileState.loading}
        tableName={tableName}
        searchingText={handleSearch}
        onViewCmmClick={(data) => handleViewCmmClick(data)}
        onTextNoteClick={(data) => handleTextNoteClick(data)}
        onLetterUploadClick={(data) => handleLetterUploadClick(data)}
        onRefreshButton={handleRefresh}
        onRowClick={(data) => handleRowClick(data)}
        isDisabled={pharmaStpFileState.loading}
        showRefreshButton={true}
        showFilterButton={true}
        filterSectionsData={filterData}
        onFilterApplyClicked={handleFilterApply}
        dropdownSectionIcon={
          <div
            className={`flex cursor-pointer items-center justify-center rounded-md border p-2 transition-colors ${
              isArchiveActive
                ? "border-gray-700 bg-gray-800 hover:bg-gray-700"
                : "border-gray-300 bg-white hover:bg-gray-50"
            }`}
          >
            <Archive
              width="18"
              height="18"
              stroke={isArchiveActive ? "#FFFFFF" : "#475467"}
            />
          </div>
        }
        onDropdownSectionIconClick={handleDropdownSectionIconClick}
        endIndexOfTable={setEndIndex}
        requestForPaginationReset={paginationResetCount}
        showInLineLoader={pharmaStpFileState.showInlineLoader}
        onAddKeyButtonClick={handleAddKeyButtonClick}
        onCallingHistoryClick={handleCallingHistoryClick}
        onThreeDotsOptionClick={handleThreeDotsOptionClick}
      />
      {openedModalId === ModalId.SFTP_DOC_MODAL && (
        <SftpDocModal onClose={handleCloseSftpDocModal} />
      )}
      {openedModalId === ModalId.TEXT_NOTE_DOC && (
        <TextNoteDoc
          onCancel={handleCloseTextNoteDocModal}
          onSave={handleSaveTextNoteDoc}
          filePath={screenshotUrl}
          loading={screenshotLoading}
          error={screenshotError}
          signedScreenshotUrl={signedScreenshotUrl}
          signedScreenshotLoading={signedScreenshotLoading}
          signedScreenshotError={signedScreenshotError}
          formattedTime={screenshotFormattedTime}
        />
      )}
      {openedModalId === ModalId.LETTER_UPLOAD_DOC && (
        <LetterUploadDoc
          onCancel={handleCloseLetterUploadDocModal}
          filePath={letterUploadScreenshotUrl}
          loading={letterUploadLoading}
          error={letterUploadError}
          formattedTime={letterUploadTimestamp}
        />
      )}
      {openedModalId === ModalId.SFTP_RERUN_MODAL && (
        <SftpRerun
          onClose={handleCloseSftpRerunModal}
          onSave={handleSaveSftpRerun}
          isRunning={isRerunning}
        />
      )}
    </>
  );
};

export default CallingWorklistTable;
